import React from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useUi } from "@jelly/lesson";
import { useIntl } from "react-intl";

import { messages } from "./inAutomotive.messages";
import styles from "./inAutomotive.module.scss";

import * as appConfig from "appConfig";
import { PhotoView } from "../../../photo-view/PhotoView";
import {
  PhysicalPrototypingModal,
  VirtualPrototypingModal,
  MusicSystemsModal,
  AudioZonesModal,
  DirectionalAlertsModal,
} from "./modals/Modals";

interface InAutomotiveProps {
  title: string;
}

export const InAutomotive: React.FC<InAutomotiveProps> = ({ title }) => {
  const { formatMessage } = useIntl();
  const ui = useUi();

  // Modal state management
  const [physicalPrototypingModalOpen, setPhysicalPrototypingModalOpen] = React.useState(false);
  const [virtualPrototypingModalOpen, setVirtualPrototypingModalOpen] = React.useState(false);
  const [musicSystemsModalOpen, setMusicSystemsModalOpen] = React.useState(false);
  const [audioZonesModalOpen, setAudioZonesModalOpen] = React.useState(false);
  const [directionalAlertsModalOpen, setDirectionalAlertsModalOpen] = React.useState(false);

  const handleClosePhysicalPrototypingModal = React.useCallback(() => setPhysicalPrototypingModalOpen(false), []);
  const handleCloseVirtualPrototypingModal = React.useCallback(() => setVirtualPrototypingModalOpen(false), []);
  const handleCloseMusicSystemsModal = React.useCallback(() => setMusicSystemsModalOpen(false), []);
  const handleCloseAudioZonesModal = React.useCallback(() => setAudioZonesModalOpen(false), []);
  const handleCloseDirectionalAlertsModal = React.useCallback(() => setDirectionalAlertsModalOpen(false), []);

  return (
    <StandardCase
      title={title}
      leftComponent={
        <ui.Scrollbars>
          <div className={styles.C8_P2_S1_Title}>{formatMessage(messages.C8_P2_S1_Description)}</div>
        </ui.Scrollbars>
      }
    >
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.photoWrapper}>
            <PhotoView
              imgSrc={`${appConfig.STATIC_ASSETS_URL}/lms/case/case08/in_automotive/car.svg`}
              imgMode={"contain"}
            />
          </div>
          <div className={styles.buttonsWrapper}>
            <div className={styles.buttonWrapper}>
              <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case08/in_automotive/p_prot.svg`} />
              <ui.Button onClick={() => setPhysicalPrototypingModalOpen(true)}>
                {formatMessage(messages.C8_P2_S1_Button1_Label)}
              </ui.Button>
            </div>
            <div className={styles.buttonWrapper}>
              <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case08/in_automotive/v_prot.svg`} />
              <ui.Button onClick={() => setVirtualPrototypingModalOpen(true)}>
                {formatMessage(messages.C8_P2_S1_Button2_Label)}
              </ui.Button>
            </div>
            <div className={styles.buttonWrapper}>
              <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case08/in_automotive/entert.svg`} />
              <ui.Button onClick={() => setMusicSystemsModalOpen(true)}>
                {formatMessage(messages.C8_P2_S1_Button3_Label)}
              </ui.Button>
            </div>
            <div className={styles.buttonWrapper}>
              <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case08/in_automotive/zones.svg`} />
              <ui.Button onClick={() => setAudioZonesModalOpen(true)}>
                {formatMessage(messages.C8_P2_S1_Button4_Label)}
              </ui.Button>
            </div>
            <div className={styles.buttonWrapper}>
              <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case08/in_automotive/alerts.svg`} />
              <ui.Button onClick={() => setDirectionalAlertsModalOpen(true)}>
                {formatMessage(messages.C8_P2_S1_Button5_Label)}
              </ui.Button>
            </div>
          </div>
        </div>

        {/* Modals */}
        <PhysicalPrototypingModal
          visible={physicalPrototypingModalOpen}
          onClose={handleClosePhysicalPrototypingModal}
        />
        <VirtualPrototypingModal visible={virtualPrototypingModalOpen} onClose={handleCloseVirtualPrototypingModal} />
        <MusicSystemsModal visible={musicSystemsModalOpen} onClose={handleCloseMusicSystemsModal} />
        <AudioZonesModal visible={audioZonesModalOpen} onClose={handleCloseAudioZonesModal} />
        <DirectionalAlertsModal visible={directionalAlertsModalOpen} onClose={handleCloseDirectionalAlertsModal} />
      </div>
    </StandardCase>
  );
};

// Export with lowercase name for backward compatibility
export const inAutomotive = InAutomotive;
